import React from 'react';
import { ContentLayout } from './ContentLayout';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X, Minus, Sparkles, Shield, Zap } from 'lucide-react';
import {
    TestimonialSection,
    FAQSection,
    EnhancedCTASection,
    Testimonial,
    FAQ
} from './SharedSections';
import { fadeInUp, staggerContainer, viewportConfig, scaleIn } from '../../utils/animationVariants';

interface ComparisonPoint {
    feature: string;
    us: boolean | string;
    them: boolean | string;
}

interface WhySwitchItem {
    title: string;
    description: string;
}

interface ComparePageProps {
    slug: string;
    competitorName: string;
    title: string;
    description: string;
    comparisonPoints: ComparisonPoint[];
    whySwitch?: WhySwitchItem[];
    testimonials?: Testimonial[];
    faqs?: FAQ[];
    onGetStarted: () => void;
}

export const ComparePage: React.FC<ComparePageProps> = ({
    competitorName,
    title,
    description,
    comparisonPoints,
    whySwitch,
    testimonials,
    faqs,
    onGetStarted
}) => {
    return (
        <ContentLayout title={title} description={description} onGetStarted={onGetStarted}>
            <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-20">
                {/* Hero */}
                <div className="text-center max-w-4xl mx-auto mb-20">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        className="inline-block px-4 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-6"
                    >
                        NoteSpace vs {competitorName}
                    </motion.div>
                    <motion.h1
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-7xl font-bold mb-8 tracking-tight"
                    >
                        {title}
                    </motion.h1>
                    <motion.p
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                        className="text-xl md:text-2xl text-slate-600 dark:text-slate-400 leading-relaxed"
                    >
                        {description}
                    </motion.p>
                </div>

                {/* Comparison Table */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl bg-white dark:bg-slate-900 mb-32"
                >
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50">
                                <th className="p-8 text-left text-slate-500 font-semibold text-lg border-b border-slate-100 dark:border-slate-800">Feature</th>
                                <th className="p-8 text-center text-blue-600 dark:text-blue-400 font-bold text-2xl bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-800/50 relative">
                                    NoteSpace
                                    <div className="absolute top-0 left-0 w-full h-1 bg-blue-500" />
                                </th>
                                <th className="p-8 text-center text-slate-700 dark:text-slate-300 font-bold text-2xl border-b border-slate-100 dark:border-slate-800">{competitorName}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {comparisonPoints.map((point, i) => (
                                <motion.tr
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.05 }}
                                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group"
                                >
                                    <td className="p-8 font-medium text-slate-900 dark:text-slate-100 text-lg">{point.feature}</td>
                                    <td className="p-8 text-center bg-blue-50/30 dark:bg-blue-900/5">
                                        <StatusCell value={point.us} checkColor="text-blue-500" />
                                    </td>
                                    <td className="p-8 text-center text-slate-500">
                                        <StatusCell value={point.them} checkColor="text-slate-400" />
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>

                {/* Why Switch Section */}
                {whySwitch && (
                    <div className="mb-32">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">Why users are making the switch</h2>
                            <p className="text-slate-600 dark:text-slate-400 text-lg">Experience a workspace built for the modern thinker.</p>
                        </div>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={viewportConfig}
                            variants={staggerContainer}
                            className="grid md:grid-cols-3 gap-8"
                        >
                            {whySwitch.map((item, i) => (
                                <motion.div
                                    key={i}
                                    variants={scaleIn}
                                    className="p-8 rounded-3xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 hover:border-blue-500/30 transition-all"
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center mb-6 shadow-sm border border-slate-100 dark:border-slate-700 text-blue-600 dark:text-blue-400">
                                        {i === 0 ? <Zap className="w-6 h-6" /> : i === 1 ? <Sparkles className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">{item.title}</h3>
                                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-lg">{item.description}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}
            </div>

            {/* Testimonials */}
            {testimonials && testimonials.length > 0 && (
                <TestimonialSection testimonials={testimonials} />
            )}

            {/* FAQs */}
            {faqs && faqs.length > 0 && (
                <FAQSection faqs={faqs} />
            )}

            {/* Final CTA */}
            <EnhancedCTASection onGetStarted={onGetStarted} />
        </ContentLayout>
    );
};

const StatusCell: React.FC<{ value: boolean | string; checkColor: string }> = ({ value, checkColor }) => {
    if (typeof value === 'boolean') {
        return value ? (
            <div className="flex justify-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`w-10 h-10 rounded-full bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center ${checkColor}`}
                >
                    <Check className="w-6 h-6 stroke-[3]" />
                </motion.div>
            </div>
        ) : (
            <div className="flex justify-center">
                <X className="w-8 h-8 text-slate-300 dark:text-slate-600 opacity-50" />
            </div>
        );
    }
    return <span className={`font-bold text-lg ${checkColor}`}>{value}</span>;
}
